import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import Navbar from "./Navbar";
import { API_ENDPOINTS } from "../config/api";
import { toast } from "react-toastify";

function Step1Email({ userData, setUserData }) {
  const [email, setEmail] = useState(userData.email || "");
  const navigate = useNavigate();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [error, setError] = useState('');
  
  // Get in Touch state
  const [contactData, setContactData] = useState({
    fullName: "",
    email: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);

  // Check user status before navigation
  const checkUserStatus = async (email) => {
    setCheckingStatus(true);
    try {
      const response = await axios.post(API_ENDPOINTS.checkUserStatus, {
        email: email
      });

      if (response.data.status === 'UNVERIFIED_ACCOUNT') {
        // User registered but not verified → Go to OTP
        toast.info("Account found! Please verify your email.");
        setUserData({ ...userData, email });
        navigate("/auth", { 
          state: { 
            email, 
            mode: 'otp', 
            message: 'Please check your email for OTP verification.',
            user: response.data.user
          } 
        });
      } else if (response.data.status === 'VERIFIED_USER') {
        // Verified user → Go to login tab
        toast.info("Welcome back! Please login.");
        setUserData({ ...userData, email });
        navigate("/auth", { 
          state: { 
            email, 
            mode: 'login', 
            message: 'Account verified. Please login.',
            user: response.data.user
          } 
        });
      } else {
        // New user → Normal signup
        setUserData({ ...userData, email });
        toast.success("Let's create your account!");
        navigate("/auth", { state: { email, mode: 'signup' } });
      }
    } catch (err) {
      // No user found OR other error → Normal signup flow
      console.log("New user flow or error:", err.response?.data);
      setUserData({ ...userData, email });
      toast.success("Email confirmed! Redirecting to signup...");
      navigate("/auth", { state: { email, mode: 'signup' } });
    } finally {
      setCheckingStatus(false);
    }
  };

  // HERO email submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Check status instead of direct navigation
    checkUserStatus(email);
  };

  // Get in Touch submit with toast notifications
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactData.fullName || !contactData.email || !contactData.message) {
      toast.error("Please fill all fields");
      return;
    }

    setContactLoading(true);

    try {
      const res = await fetch(API_ENDPOINTS.contact, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send message");

      toast.success("Message sent successfully! We'll get back to you soon. 🎉");
      setContactData({ fullName: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.message || "Failed to send message. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      {/* HERO */}
      <section className="hero">
        <div className="container hero-content">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1>Elevate Your VoIP Call Experience</h1>
              <p>
                Make every conversation count with our innovative Speaking app.
                Record and use pre-recorded phrases instantly during calls to
                maintain professionalism and efficiency.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="d-flex flex-wrap gap-2">
                  <input
                    type="email"
                    className="form-control form-control-lg"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={checkingStatus}
                    style={{ minWidth: "250px", maxWidth: "400px" }}
                  />
                  <button
                    type="submit"
                    className="btn bg-light text-dark btn-lg px-4 fw-bold"
                    disabled={checkingStatus}
                  >
                    {checkingStatus ? "Checking..." : "Get Started"}
                  </button>
                </div>
              </form>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <a
                  href="https://play.google.com/store/apps"
                  target="_blank"
                  rel="noreferrer"
                  className="btn bg-light text-dark btn-lg px-4"
                >
                  <i className="bi bi-google-play"></i> Play Store
                </a>
                <a
                  href="https://www.apple.com/in/app-store"
                  target="_blank"
                  rel="noreferrer"
                  className="btn bg-light text-dark btn-lg px-4"
                >
                  <i className="bi bi-apple"></i> App Store
                </a>
              </div>
            </div>

            <div className="col-lg-6 d-none d-lg-block text-center"></div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-5 bg-light">
        <div className="container">
          <div className="row mb-5 justify-content-center text-center">
            <div className="col-lg-7">
              <h2 className="fw-bold mb-3">
                Smart Call Management Features
              </h2>
              <p className="text-muted lead">
                Our innovative VoIP solution streamlines business communications
                with pre-recorded responses and professional tools.
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="feature-box">
                <div className="feature-icon"><i className="bi bi-mic-fill"></i></div>
                <h4>Pre-recorded Phrases</h4>
                <p>
                  Record common responses and organize them with our intuitive
                  PLU system for quick access during calls.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="feature-box">
                <div className="feature-icon"><i className="bi bi-telephone-fill"></i></div>
                <h4>Seamless VoIP Integration</h4>
                <p>
                  Maintain your regular phone number while using data for VoIP
                  calls.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="feature-box">
                <div className="feature-icon"><i className="bi bi-folder-fill"></i></div>
                <h4>Categorized Responses</h4>
                <p>
                  Organize phrases by category for instant access during calls.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="feature-box">
                <div className="feature-icon"><i className="bi bi-speedometer2"></i></div>
                <h4>Quick Access System</h4>
                <p>
                  Tap a number 1–99 to instantly play your pre-recorded phrase.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="feature-box">
                <div className="feature-icon"><i className="bi bi-wifi"></i></div>
                <h4>Dual Network Support</h4>
                <p>
                  Use cellular data or Wi-Fi while maintaining your regular
                  carrier number.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="feature-box">
                <div className="feature-icon"><i className="bi bi-person-fill"></i></div>
                <h4>Professional Impressions</h4>
                <p>
                  Ensure consistent, professional responses every time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-5 bg-gradient-light">
        <div className="container">
          <div className="row align-items-center mb-5">
            <div className="col-lg-6">
              <h2 className="fw-bold mb-3">How Speaking Works</h2>
              <p className="lead mb-4">
                Our innovative VoIP system maintains your regular phone number
                while enhancing call capabilities.
              </p>

              <div className="d-flex mb-4">
                <div className="me-3">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    1
                  </div>
                </div>
                <div>
                  <h5>Record Your Phrases</h5>
                  <p>Create a library of pre-recorded responses and assign each a PLU.</p>
                </div>
              </div>

              <div className="d-flex mb-4">
                <div className="me-3">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    2
                  </div>
                </div>
                <div>
                  <h5>Organize By Category</h5>
                  <p>Group phrases by type — greetings, product info, FAQs.</p>
                </div>
              </div>

              <div className="d-flex mb-4">
                <div className="me-3">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    3
                  </div>
                </div>
                <div>
                  <h5>Use During Live Calls</h5>
                  <p>Tap the corresponding PLU number to play your phrase.</p>
                </div>
              </div>

              <div className="d-flex">
                <div className="me-3">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                  >
                    4
                  </div>
                </div>
                <div>
                  <h5>Network Flexibility</h5>
                  <p>Use cellular data or Wi-Fi; the app auto-optimizes.</p>
                </div>
              </div>
            </div>

            <div className="col-lg-6 text-center"></div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-5">
        <div className="container">
          <div className="row mb-5 text-center">
            <h2 className="fw-bold">Simple, Transparent Pricing</h2>
            <p className="text-muted">
              Choose the plan that best fits your business needs.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="pricing-card h-100">
                <h4>Starter</h4>
                <div className="price">$9.99<span>/month</span></div>
                <ul className="mt-3">
                  <li>100 minutes/month</li>
                  <li>50 PLU</li>
                  <li>Basic analytics</li>
                </ul>
                <a href="#" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
            </div>

            <div className="col-md-4">
              <div className="pricing-card featured h-100">
                <h4>Professional</h4>
                <div className="price">$19.99<span>/month</span></div>
                <ul className="mt-3">
                  <li>500 minutes/month</li>
                  <li>200 PLU</li>
                  <li>Advanced analytics</li>
                </ul>
                <a href="#" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
            </div>

            <div className="col-md-4">
              <div className="pricing-card h-100">
                <h4>Enterprise</h4>
                <div className="price">$49.99<span>/month</span></div>
                <ul className="mt-3">
                  <li>Unlimited minutes</li>
                  <li>Unlimited PLU</li>
                  <li>Real-time analytics</li>
                </ul>
                <a href="#" className="btn btn-outline-primary w-100">Get Started</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-5 bg-light">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="fw-bold mb-3">Get in Touch</h3>
              <form className="row g-3" onSubmit={handleContactSubmit}>
                <div className="col-md-6">
                  <label className="form-label">Full Name</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Your name"
                    value={contactData.fullName}
                    onChange={(e) =>
                      setContactData({ ...contactData, fullName: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    className="form-control"
                    type="email"
                    placeholder="Your email"
                    value={contactData.email}
                    onChange={(e) =>
                      setContactData({ ...contactData, email: e.target.value })
                    }
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="How can we help?"
                    value={contactData.message}
                    onChange={(e) =>
                      setContactData({ ...contactData, message: e.target.value })
                    }
                  ></textarea>
                </div>
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={contactLoading}
                  >
                    {contactLoading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="row gy-4">
            <div className="col-lg-4">
              <h4 className="mb-3">
                <img
                  src="https://lh3.googleusercontent.com/d/1VG0Squbhv2_4nXDsYwG0vkrYLAMuN8EX"
                  width="40"
                  alt="Logo"
                  style={{ marginBottom: "10px" }}
                />{" "}
                Hola!...Speaking..
              </h4>
              <p className="text-white-50">
                Revolutionizing business communications with our innovative PLU system.
              </p>
            </div>

            <div className="col-lg-2 footer-links">
              <h5>Product</h5>
              <ul className="list-unstyled">
                <li><a href="#features">Features</a></li>
                <li><a href="#how-it-works">How it works</a></li>
                <li><a href="#pricing">Pricing</a></li>
              </ul>
            </div>

            <div className="col-lg-6">
              <h5 className="mb-3">Contact Information</h5>
              <p><i className="bi bi-geo-alt me-2"></i>Whistler BC, CA, Vancouver</p>
              <p><i className="bi bi-envelope me-2"></i>brianwhistler@gmail.com</p>
              <p><i className="bi bi-clock me-2"></i>Mon–Fri: 9am–6pm</p>
            </div>

            <div className="col-lg-12">
              <p className="text-white-50 mb-0">
                © 2025 Hola!...Speaking.. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Step1Email;
